/**
 * BlogPostCard — single-post summary card used by the index, category,
 * and tag pages.
 *
 * Renders title + excerpt + author + reading time + an article-shape
 * `<a href="/blog/<slug>">` wrapper. Consumes canonical
 * `@puffin-core/shared-styles` utility classes only (CSS-001).
 *
 * @module @puffin-core/marketing-shell/blog/BlogPostCard
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import formatPublishedDate from './formatPublishedDate.js';

export default function BlogPostCard({
  post,
  hrefPrefix = '/blog',
  readingMinutesLabel,
  locale = 'en-US',
}) {
  const href = `${hrefPrefix}/${post.slug}`;
  // Fall back to the branded cover when there is no editorial image OR the
  // image fails to load (e.g. a not-yet-deployed absolute URL) — the card
  // should never show a broken-image box.
  const [imageFailed, setImageFailed] = useState(false);
  const showImage = Boolean(post.featuredImageUrl) && !imageFailed;

  // Each optional slot resolves to a node (or nothing) before the return, so
  // the card's returned tree reads as its structure rather than as a stack of
  // ternaries.
  const cover = showImage ? (
    <img
      src={post.featuredImageUrl}
      alt=""
      className="post-card-image"
      loading="lazy"
      onError={() => setImageFailed(true)}
    />
  ) : (
    // No editorial image — render an on-brand gradient cover so the
    // card never shows a blank gap. The title initial anchors it.
    <span className="post-card-cover" aria-hidden="true">
      <span className="post-card-cover-mark">{post.title?.charAt(0) || 'P'}</span>
    </span>
  );

  const excerpt = post.excerpt ? <p className="post-card-excerpt">{post.excerpt}</p> : null;

  const author = post.authorName ? (
    <span className="post-card-author">{post.authorName}</span>
  ) : null;

  const published = post.publishedAt ? (
    <time className="post-card-published" dateTime={post.publishedAt}>
      {formatPublishedDate(post.publishedAt, locale)}
    </time>
  ) : null;

  const reading =
    typeof post.readingMinutes === 'number' && readingMinutesLabel ? (
      <span className="post-card-reading">
        {readingMinutesLabel.replace('{{minutes}}', String(post.readingMinutes))}
      </span>
    ) : null;

  return (
    <article className="post-card feature-card feature-card-outlined">
      <Link to={href} className="post-card-image-link" aria-hidden="true" tabIndex={-1}>
        {cover}
      </Link>
      <div className="post-card-body">
        <h3 className="post-card-title">
          <Link to={href} className="post-card-title-link">
            {post.title}
          </Link>
        </h3>
        {excerpt}
        <div className="post-card-meta">
          {author}
          {published}
          {reading}
        </div>
      </div>
    </article>
  );
}

BlogPostCard.propTypes = {
  post: PropTypes.shape({
    slug: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    excerpt: PropTypes.string,
    authorName: PropTypes.string,
    publishedAt: PropTypes.string,
    readingMinutes: PropTypes.number,
    featuredImageUrl: PropTypes.string,
  }).isRequired,
  hrefPrefix: PropTypes.string,
  readingMinutesLabel: PropTypes.string,
  locale: PropTypes.string,
};
